const HOME = /^\/(?:us|uk|ie|ca|au|nz|za|de|fr|es|it|pt|dk|se|mt|br|co|ec|mx|be|pl|sk|no|bg|fi|nl|at|cz|in|ch)?\/?$/i;

/**
 * Future releases can recover an old homepage restored from the browser cache.
 * Never clear storage or refresh booking/account pages, edited forms or dialogs.
 */
export function installReleaseFreshnessGuard({ baseUrl = "/", win = window, doc = document,
  fetcher = (...args) => win.fetch(...args), reload = () => win.location.reload() } = {}) {
  const base = baseUrl.replace(/\/+$/, "");
  const currentSrc = doc.querySelector('script[type="module"][src]')?.getAttribute("src");
  if (!currentSrc) return () => {};
  const entry = new URL(currentSrc, win.location.href).pathname;
  let edited = false;
  let inFlight = false;
  const home = () => HOME.test(win.location.pathname.slice(base.length));
  const safe = () => home() && !edited &&
    !doc.querySelector('[role="dialog"], [aria-modal="true"], [contenteditable="true"]');
  const edit = () => { edited = true; };
  const interact = event => {
    if (event.target?.closest?.('form, input, textarea, select, [aria-haspopup="dialog"]')) edit();
  };
  async function check() {
    if (!safe() || inFlight) return;
    inFlight = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetcher(`${base || ""}/`, {
        method: "HEAD", cache: "no-store", credentials: "same-origin", signal: controller.signal,
      });
      const advertised = response.headers.get("X-App-Entry");
      if (!response.ok || !advertised || !safe()) return;
      const next = new URL(advertised, win.location.href);
      if (next.origin !== win.location.origin || next.pathname === entry ||
          !next.pathname.startsWith(`${base}/assets/`) || !next.pathname.endsWith(".js")) return;
      // Unavailable storage means no automatic reload: don't risk a loop.
      const key = `bac-release-refresh:${next.pathname}`;
      if (win.sessionStorage.getItem(key)) return;
      win.sessionStorage.setItem(key, "1");
      reload();
    } catch { /* A freshness probe must never break an otherwise usable page. */ }
    finally { clearTimeout(timer); inFlight = false; }
  }
  const restore = event => { if (event.persisted) void check(); };
  win.addEventListener("pageshow", restore);
  doc.addEventListener("input", edit, true);
  doc.addEventListener("change", edit, true);
  doc.addEventListener("pointerdown", interact, true);
  // Also covers a cached document opened directly, not only history restores.
  void check();
  return () => {
    win.removeEventListener("pageshow", restore);
    doc.removeEventListener("input", edit, true);
    doc.removeEventListener("change", edit, true);
    doc.removeEventListener("pointerdown", interact, true);
  };
}
